// libraries
import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react'
import { keyBy, uniqBy } from 'lodash'
import PropTypes from 'prop-types'
import { useNavigate } from 'react-router-dom'
import { useFormContext, useWatch } from 'react-hook-form'
import { useSelector, useDispatch } from 'react-redux'
import { v4 as uuidv4 } from 'uuid'
import Dinero from 'dinero.js'
import {
  Autocomplete,
  Box,
  Typography,
  Stack,
  Button,
  Grid,
  InputAdornment,
  IconButton,
  InputLabel,
  Tooltip,
  Skeleton,
  CircularProgress,
  TextField,
} from '@mui/material'
import {
  Delete as DeleteIcon,
  AddCircle as AddCircleIcon,
  Warning as WarningIcon,
  NoteAlt as NoteIcon,
  Link as LinkIcon,
} from '@mui/icons-material'
import EditIcon from '@mui/icons-material/Edit'
import { useTheme } from '@mui/styles'
import { useTranslation } from 'react-i18next'
import { Controller } from 'react-hook-form'

// state
import { showFormModal } from 'state/ui'
import { clearCustomer, setCustomer } from 'state/customer'
import {
  setPurchaseOrderInfo,
  setScrapVehicles,
  clearTransportationInformation,
  resetScrapVehiclesEdited,
  setActiveDialogState,
  setInboundTicketId,
} from 'state/inboundTicket'

// specific components
import LicenseAvatar from 'components/containers/inbound-tickets/components/LicenseAvatar'
import MaterialEntry from 'components/containers/inbound-tickets/components/MaterialEntry'
import SplitTruckAndMaterialDifferentialBlade from 'components/containers/inbound-tickets/split-weights/SplitTruckAndMaterialDifferentialBlade'
import FreightCostBlade from 'components/containers/freight/freight-cost/FreightCostBlade'
import CustomerLicenseListItem from 'components/containers/inbound-tickets/components/CustomerLicenseListItem'
import CustomerInfoPopover from 'components/generic/popovers/CustomerInfoPopover'
import DispatchTicketFormArea from 'components/containers/inbound-tickets/components/LinkDispatchTicketFormArea'

// generic components
import Input from 'components/generic/form/Input'
import DatePickerV2 from 'components/generic/form/DatePickerV2'
import CustomerAutocomplete from 'components/generic/form/AsyncAutocomplete/CustomerAutocomplete'
import VehicleAutocomplete from 'components/generic/form/VehicleAutocomplete'
import SimpleMaterialsTable from 'components/generic/printable/table/SimpleMaterialsTable'
import NotesModal, { formatNotes } from 'components/generic/modals/dialog-modal/NotesModal'
import NotesInput from 'components/generic/inputs/NotesInput'
import AlertModal from 'components/generic/modals/dialog-modal/AlertModal'
import FileUploader from 'components/generic/file-uploader/FileUploader'

// helpers and utils
import {
  SCALE_MATERIAL_VIEW_OPTIONS,
  CHURN_ZERO_EVENTS as cz,
  CUSTOMERS_FILTERS,
  IBT_VIEW_OPTIONS,
  TRANSPORTATION_TYPE_OPTIONS,
  DEFAULT_AXLE_DATA,
  ROUNDING_PRECISIONS,
  GS_ERROR_CODE,
  TICKET_STATUSES,
  IBT_ROW_TYPES,
  BILL_STATUSES,
} from 'utils/constants'
import { useScrapVehicles } from 'utils/custom-hooks/useScrapVehicles'
import useUpdateScaleIBTPrices from 'utils/custom-hooks/useUpdateScaleIBTPrices'
import FORM_MODALS from 'utils/ui/modals'
import { trackEvent } from 'utils/tracking'
import { getCustomerId, getTruckTotal, getDateTime } from 'components/containers/inbound-tickets/utils'
import { calcAdjustments, dispatchSnackbar, isDateInPast, setDecimalPrecision, locationIsNotEmpty } from 'utils/helpers'
import { roundTo } from 'utils/data-manipulation/valueFormatter'
import { calculateFeePromotionValue } from 'utils/fee-promotion-calculator'
import { formatScrapVehicles, formatVehicle, addToDate } from 'utils/data-manipulation/common'
import { useCalculateFreightCost } from 'utils/custom-hooks/useCalculateFreightCost'
import useAutosave from 'components/containers/inbound-tickets/helpers/autosave'
import { usePatchDispatchTicketMutation } from 'services/dispatch'
import { DEFAULT_PDF_WIDTH } from 'utils/constants'
import { useGetPurchaseOrdersV2Query } from 'services/purchaseOrders'

import FeeOrPromotionModal from './components/FeeOrPromotionModal'

const InboundTicketScaleContainer = ({
  isUpdate,
  inboundTicketId,
  inboundTicketData,
  inboundTicketNotesRef,
  inboundTicketNotes,
  onSaveNotes,
  billData,
  customerData,
  carrierData,
  supplierRef,
  customerDataRef,
  createInboundTicket,
  updateInboundTicket,
  setErrorMessage,
  statusIsPaidOrVoided,
  statusIsVoided,
  freightBillIsPaid,
  isDirty,
  isCustomerInfoDirty,
  hasEditedMaterials,
  setHasEditedMaterials,
  wrappedUpdateTicketActionsAndStatus,
  isAutosaveEnabled,
  onAddVehicle,
  vehicleData,
  updateVehicleWithCustomerAndIBT,
  userIsInactive,
  isTransfer,
  pricePrepend,
  carrierPricePrepend,
  defaultRetailCustomer,
  purchaseOrderData,
  onEditScrapVehicle,
  hasDataConflict,
  ticketHashRef,
  errorsTareRequired,
  setIsFetchingMaterialPrices,
  isInLockedAccountingPeriodV1,
  enableFePdfs,
  handlePrint,
  purchaseOrderLocationId,
  feesAndPromotionsData,
  checkDoNotBuyViolation,
  onTotalUpdate,
}) => {
  const { canUploadFilesIBT, appRouteLoadBoard } = useSelector(state => state.featureFlags?.variables || {})

  const { t } = useTranslation('inboundTicket')
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const methods = useFormContext()
  const theme = useTheme()
  const { purchaseOrderNumber, purchaseOrderId } = useSelector(state => state.inboundTicket.transportationInformation)
  const { defaultYardUm, settings } = useSelector(state => state.settings)

  const purchaseOrderShouldDirty = useRef(false)
  const { control, watch, setValue, getValues, formState, reset, clearErrors } = methods
  const { errors, isValid } = formState
  const [customerVehicle, setCustomerVehicle] = useState(null)
  const [materialEntryViewMode, setMaterialEntryViewMode] = useState(SCALE_MATERIAL_VIEW_OPTIONS.BUTTONS)
  const [inboundTicketMaterials, setInboundTicketMaterials] = useState([])
  const [selectedMaterial, setSelectedMaterial] = useState({})
  const [truckWeight, setTruckWeight] = useState({
    gross: { amount: null, units: defaultYardUm },
    tare: { amount: null, units: defaultYardUm },
  })
  const selectedCustomer = watch('customer.name', {})
  const customerLocationId = watch('customer.locationId')
  const selectedCarrier = watch('transportationInfo.carrierLookup')
  const transportationType = watch('transportationInfo.transportationType')
  const cost = watch('freightCost.cost')
  const userSet = watch('freightCost.userSet')
  const chargeback = watch('freightCost.chargeBack')
  const userChargebackSet = watch('freightCost.userChargebackSet')
  const [hasSupplier, setHasSupplier] = useState(false)
  const purchaseOrderInput = useWatch({
    control,
    name: 'transportationInfo.purchaseOrderNumber',
  })
  const effectiveDateInput = useWatch({ control, name: 'customer.effectiveDate' })
  const { customer: supplier, customers } = useSelector(state => state.customer)
  const {
    defaultNumberOfAxles,
    enableAxleWeights,
    addCustomersOnInboundTickets: canAddCustomer,
  } = settings?.purchaseSettings?.inboundTickets || {}
  const { lockAccountingPeriod } = settings?.overviewSettings?.overview || {}
  const [showRemoveCustomerModal, setShowRemoveCustomerModal] = useState(false)
  const [openNotes, setOpenNotes] = useState(false)
  const [locationOptions, setLocationOptions] = useState([])
  const [differentialAmount, setDifferentialAmount] = useState(0)
  const [openSplitWeightsBlade, setOpenSplitWeightsBlade] = useState(false)
  const [openFreightCostBlade, setOpenFreightCostBlade] = useState(false)
  const [freightCostFieldLength, setFreightCostFieldLength] = useState(0)
  const { supplementalPriceSheetIds } = useSelector(state => state.materials)
  const inboundTicketMaterialsRef = useRef([])
  const initialRender = useRef(true)
  const idempotencyKey = useRef(uuidv4())
  const { newCustomerVehicle: newCustomerVehicleState } = useSelector(state => state.inboundTicket)
  const [showUnlinkModal, setShowUnlinkModal] = useState(false)
  const [isUnlinking, setIsUnlinking] = useState(false)
  const [contactOptions, setContactOptions] = useState([])
  const [patchDispatchTicket] = usePatchDispatchTicketMutation()
  const [selectedFeeOrPromotionId, setSelectedFeeOrPromotionId] = useState(null)
  const [currentTotal, setCurrentTotal] = useState(0)

  const { scrapVehiclesRef, newScrapVehicle } = useScrapVehicles({})

  const netWeight = useMemo(
    () =>
      inboundTicketMaterials.reduce((aggregate, material) => {
        const materialGross = material?.weightAndPrice?.gross?.amount || 0
        const materialTare = material?.weightAndPrice?.tare?.amount || 0
        const materialWeightAdjustments = calcAdjustments(material).weightAdjustments
        const net = roundTo(materialGross - materialTare - materialWeightAdjustments, ROUNDING_PRECISIONS.WEIGHT)
        return roundTo(aggregate + net, ROUNDING_PRECISIONS.WEIGHT)
      }, 0),
    [inboundTicketMaterials],
  )

  const { isFetching: isFetchingPrices, materialTypesData } = useUpdateScaleIBTPrices({
    supplementalPriceSheetIds,
    isDirty,
    inboundTicketMaterials,
    setInboundTicketMaterials,
  })

  useEffect(() => {
    setIsFetchingMaterialPrices(isFetchingPrices)
  }, [isFetchingPrices])

  const onSaveNotesLocal = notes => {
    setOpenNotes(false)
    onSaveNotes(notes.map(n => ({ ...n, note: n.name })))
  }

  const handleUnlinkDispatchTicket = async () => {
    setIsUnlinking(true)

    await patchDispatchTicket({
      patchDispatchTicketBody: {
        inboundTicketId: null,
        hash: inboundTicketData?.linkedDispatchTicket?.hash,
      },
      dispatchTicketId: inboundTicketData?.linkedDispatchTicket?.id,
      inboundTicketId: inboundTicketId,
      assetIds: [],
    })
      .unwrap()
      .then(() => {
        dispatchSnackbar(dispatch, 'success', 'Dispatch Ticket successfully unlinked.')
      })
      .catch(error => {
        let errorMessage = 'There was an error unlinking the Dispatch Ticket.'

        if (error?.data?.code === GS_ERROR_CODE.CONCURRENCY_VIOLATION) {
          errorMessage = `Dispatch ticket has been updated by another user. Please refresh your page and try again.`
        }

        dispatchSnackbar(dispatch, 'error', errorMessage)
      })
      .finally(() => {
        setShowUnlinkModal(false)
      })
  }

  const removeCustomer = () => {
    dispatch(clearCustomer())
    setValue('customer.name', '', { shouldDirty: true })
    setValue('customer.nametext', '', { shouldDirty: true })
    setLocationOptions([])
    setValue('customer.locationId', '', { shouldDirty: true })
    setValue('customer.contactId', '', { shouldDirty: true })
    setValue('customer.vehicle', '', { shouldDirty: true })
    setCustomerVehicle(null)
  }

  const removePurchaseOrder = () => {
    dispatch(clearTransportationInformation())
    setValue('transportationInfo.purchaseOrderNumber', '')
    setValue('transportationInfo.purchaseOrderId', '')
  }

  const handleRemoveCustomer = () => {
    if (purchaseOrderInput) {
      setShowRemoveCustomerModal(true)
    } else {
      removeCustomer()
    }
  }

  /**
   * Handles the Save button behavior depending on purchase order number value
   */
  useEffect(() => {
    const formattedPurchaseOrderNumber = purchaseOrderNumber?.toString() || ''
    setValue('transportationInfo.purchaseOrderNumber', formattedPurchaseOrderNumber, {
      shouldDirty: purchaseOrderShouldDirty.current,
    })
    setValue('transportationInfo.purchaseOrderId', purchaseOrderId || '', {
      shouldDirty: purchaseOrderShouldDirty.current,
    })
  }, [purchaseOrderNumber])

  /**
   * Watch material information and push to ref.
   */
  useEffect(() => {
    inboundTicketMaterialsRef.current = inboundTicketMaterials
  }, [inboundTicketMaterials])

  const feeOrPromotionForm = useWatch({
    control,
    name: 'feeOrPromotion',
    defaultValue: {},
  })

  /**
   * formats the fee or promotion data to be used in the table
   */
  const feesAndPromotionsFormatted = useMemo(() => {
    return Object.values(feeOrPromotionForm).map(item => ({
      ...item,
      rowType: item.type.toLowerCase(),
      showPaymentMethodSpecificIcon:
        inboundTicketData?.status?.value === TICKET_STATUSES.PAID.id ? false : item.eligiblePaymentTypes?.length < 4,
      canEdit: !statusIsPaidOrVoided(),
    }))
  }, [feeOrPromotionForm, inboundTicketData?.status?.value])

  /**
   * Watch customer vehicle information and set customer
   */
  useEffect(() => {
    if (customerVehicle?.customerId && customerVehicle.customerId !== customerData?.id) {
      setValue('customer.name', {
        id: customerVehicle.customerId,
        customerCommonIdentifierString: '',
        supplierType: '',
      })
    }
  }, [customerVehicle])

  /**
   * Watch new customer vehicle state and set
   */
  useEffect(() => {
    if (newCustomerVehicleState?.id) {
      setValue('customer.vehicle', newCustomerVehicleState, {
        shouldDirty: true,
      })
      setCustomerVehicle(newCustomerVehicleState)
    }
  }, [newCustomerVehicleState?.id])

  /**
   * Clear customer-related fields when customer changes via "Use This Customer"
   */
  useEffect(() => {
    if (supplier && supplier.customerCommonIdentifierString) {
      const currentCustomer = getValues('customer.name')
      // Check if customer has actually changed (different ID) and there was already a customer selected
      const hasCustomerChanged = currentCustomer?.id && currentCustomer.id !== supplier.id

      if (hasCustomerChanged) {
        setValue('customer.contactId', '', { shouldDirty: true })
        setValue('customer.locationId', '', { shouldDirty: true })
        setLocationOptions([])
        setContactOptions([])
        setCustomerVehicle(null)
      }
    }
  }, [supplier])

  // Watch vehicle field and clear local state when it's cleared
  const vehicleField = useWatch({ control, name: 'customer.vehicle' })
  useEffect(() => {
    if (vehicleField === null || vehicleField === '') {
      setCustomerVehicle(null)
    }
  }, [vehicleField])

  /**
   * Prepare request body to create/update endpoint.
   */
  const handleTotalUpdate = total => {
    if (currentTotal !== total) {
      setCurrentTotal(total)
      if (onTotalUpdate) onTotalUpdate(total)
    }
  }

  const formatData = (data, customerId, ticketId, paymentId, dataHash) => {
    console.log('=== formatData: current inboundTicketMaterials ===', inboundTicketMaterials)
    const materialRows = inboundTicketMaterialsRef.current
      .filter(material => material.materialTypeId !== undefined)
      .map(material => {
        const images = {
          grossImages: material.status?.grossImages,
          tareImages: material.status?.tareImages,
        }

        const customerPreferredPriceAmount = roundTo(
          material?.weightAndPrice?.customerPreferredPrice?.amount || 0,
          ROUNDING_PRECISIONS.PRICE,
        )
        const netPriceAmount = roundTo(material?.weightAndPrice?.netPrice?.amount || 0, ROUNDING_PRECISIONS.PRICE)

        return {
          ...material,
          ...images,
          weightAndPrice: {
            ...material?.weightAndPrice,
            um: material?.weightAndPrice?.um?.toLowerCase(),
            customerPreferredUm: material?.weightAndPrice?.customerPreferredUm?.toLowerCase(),
            customerPreferredPrice: {
              amount: customerPreferredPriceAmount,
              precision: 4,
            },
            netPrice: {
              amount: netPriceAmount,
              precision: 4,
            },
            gross: {
              ...material?.weightAndPrice?.gross,
              amount: Number(material?.weightAndPrice?.gross?.amount),
            },
            tare:
              material?.weightAndPrice?.tare === null
                ? null
                : {
                    ...material?.weightAndPrice?.tare,
                    amount: Number(material?.weightAndPrice?.tare?.amount),
                  },
          },
        }
      })
    const scrapVehicleRows =
      scrapVehiclesRef.current?.map(vehicle => ({
        ...vehicle,
        rowIndex: inboundTicketMaterialsRef.current?.find(material => material.id === vehicle.rowId)?.rowIndex,
      })) || []
    const totalGross = materialRows.reduce((aggregate, material) => {
      const gross = Number(material?.weightAndPrice?.gross?.amount) || 0
      return roundTo(aggregate + gross, ROUNDING_PRECISIONS.WEIGHT)
    }, 0)
    const totalTare = materialRows.reduce((aggregate, material) => {
      const tare = Number(material?.weightAndPrice?.tare?.amount || 0) || 0
      return roundTo(aggregate + tare, ROUNDING_PRECISIONS.WEIGHT)
    }, 0)
    const totalNet = materialRows.reduce((aggregate, material) => {
      const materialGross = material?.weightAndPrice?.gross?.amount || 0
      const materialTare = material?.weightAndPrice?.tare?.amount || 0
      const materialWeightAdjustments = calcAdjustments(material).weightAdjustments
      const net = roundTo(materialGross - materialTare - materialWeightAdjustments, ROUNDING_PRECISIONS.WEIGHT)
      return roundTo(aggregate + net, ROUNDING_PRECISIONS.WEIGHT)
    }, 0)
    const totalDeductions = materialRows.reduce((aggregate, material) => {
      const deduction = calcAdjustments(material).weightAdjustments
      return aggregate + deduction
    }, 0)
    const netValue = materialRows.reduce((aggregate, material) => {
      const materialValue = Dinero({
        ...material?.weightAndPrice?.netValue,
      }).toUnit()
      return roundTo(aggregate + materialValue, ROUNDING_PRECISIONS.COST)
    }, 0)

    const images = {
      grossImages: data?.truckWeight?.gross?.grossImages,
      tareImages: data?.truckWeight?.tare?.tareImages,
    }

    const truckWeightValues = {
      gross: data?.truckWeight?.gross || truckWeight?.gross,
      tare: data?.truckWeight?.tare || truckWeight?.tare,
    }

    const formattedData = {
      ...(inboundTicketId && { id: inboundTicketId }),
      hash: dataHash,
      materials: materialRows,
      effectiveDate: getDateTime(data.customer?.effectiveDate, inboundTicketData?.effectiveDate).toISOString(),
      status: {
        value: data?.status?.value,
        date: new Date().toISOString(),
        ...images,
      },
      customer: {
        customerCommonIdentifierString: data.customer?.nametext,
        ...(customerId && { id: customerId }),
      },
      numberOfPassengers: data.customer?.passengers,
      customerId: customerId || null,
      contactId: data.customer?.contactId || null,
      ticketId,
      paymentId,
      carrierId: data.transportationInfo?.carrierLookup?.id || null,
      transportationInfo: {
        billOfLading: '',
        driverName: '',
        trailerNumber: '',
        containerNumber: '',
        transportationType: '',
        ...data.transportationInfo,
        isPickup: data.transportationInfo?.isPickup || 'false',
        purchaseOrderNumber: parseInt(data.transportationInfo?.purchaseOrderNumber, 10) || null,
        purchaseOrderId: data.transportationInfo?.purchaseOrderId || null,
        carrierNumber: data?.customer?.carrierNumber,
      },
      scrapVehicles: scrapVehicleRows,
      truckWeight: {
        isManualGross: data?.truckWeight?.isManualGross,
        isManualTare: data?.truckWeight?.isManualTare,
        ...truckWeightValues,
      },
      loadWeight: {
        gross: totalGross,
        tare: totalTare,
        deductions: totalDeductions,
        net: totalNet,
      },
      netWeight: totalNet,
      taxRate: customerDataRef?.current?.id === supplierRef?.current?.id ? customerDataRef?.current?.taxRate : '0',
      vehicleId: data.customer?.vehicle?.id || null,
      locationId: data.customer?.locationId || null,
      docs: data?.docs,
      uploadcareGroupId: data?.uploadcareGroupId,
      freightCost: {
        cost: data.freightCost?.cost || 0,
        chargeBack: data.freightCost?.chargeBack || 0,
        userSet: data.freightCost?.userSet || false,
        userChargebackSet: data.freightCost?.userChargebackSet || false,
        id: data.freightCost?.id || undefined,
      },
      netValue: netValue - (data?.freightCost?.chargeBack || 0),
      axleWeights: data.axleWeights,
    }

    const feeOrPromotionFormState = getValues('feeOrPromotion') || {}
    const feeOrPromotionData = Object.values(feeOrPromotionFormState).map(item => ({
      ...item,
      settingId: item.id,
      appliedAutomatically: true, // always true for now
    }))

    formattedData.feeOrPromotion = feeOrPromotionData
    return formattedData
  }

  const handleSuccess = async ({ resIBT, data, customerId, isNew = false, saveAction }) => {
    reset({
      ...data,
      customer: {
        ...(data.customer || {}),
        name: null,
      },
    })
    ticketHashRef.current.value = resIBT.hash
    setHasEditedMaterials(false)
    trackEvent({
      eventName: isNew ? cz.CREATE_INBOUND_TICKET : cz.UPDATE_INBOUND_TICKET,
      customFields: { container: 'scale' },
    })
    if (data.customer?.vehicle?.id) {
      await updateVehicleWithCustomerAndIBT(data.customer?.vehicle, customerId, resIBT.id, resIBT.vehicleId)
    }
    dispatch(resetScrapVehiclesEdited())
    dispatchSnackbar(dispatch, 'success', 'Changes Saved')

    if (saveAction === 'print') {
      const docTitle = `Scale Ticket #${resIBT?.ticketId} | ${TICKET_STATUSES[resIBT?.status?.value?.toString()]?.name}`
      if (enableFePdfs) {
        const paperWidth = localStorage.getItem('paperSize') ?? DEFAULT_PDF_WIDTH
        handlePrint(
          `/core-api/purchases/inbound-tickets/${resIBT?.id}/pdf/IBT_SCALE_TICKET?width=${paperWidth}`,
          docTitle,
        )
      } else {
        dispatch(
          setActiveDialogState({
            title: 'Scale Ticket (Full Page)',
            action: 0,
            dialogTitle: `Scale Ticket #${resIBT?.ticketId} | ${
              TICKET_STATUSES[resIBT?.status?.value?.toString()]?.name
            }`,
          }),
        )
      }
    }

    if (saveAction === 'pay') {
      if (isNew) navigate(`/app/purchases/ticket-library/${resIBT.id}`)
      else navigate(`/app/purchases/ticket-library/${resIBT.id}/payment`)
    }

    if (saveAction === 'invoice') {
      dispatch(setInboundTicketId(resIBT.id))

      if (billData?.invoiceId) {
        navigate(`/app/sales/invoices/${billData.invoiceId}`)
      } else {
        navigate('/app/sales/invoices/new')
      }
    }

    if (!saveAction && isNew) {
      navigate(`/app/purchases/ticket-library/${resIBT.id}`)
    }
  }

  const handleError = ({ error, isNew = false }) => {
    const errorMsg =
      error?.data?.error ||
      error?.error ||
      error?.message ||
      `An error occurred ${isNew ? 'creating' : 'updating'} the ticket.`
    trackEvent({
      eventName: isNew ? cz.CREATE_INBOUND_TICKET : cz.UPDATE_INBOUND_TICKET,
      errorMsg,
      customFields: { container: 'scale' },
    })
    setErrorMessage(errorMsg)
  }
  /**
   * Handle submit button click.
   */
  const onSubmit = async (data, _, saveAction = '') => {
    if (saveAction === 'pay' && errorsTareRequired(inboundTicketMaterialsRef.current, IBT_VIEW_OPTIONS.SCALE)) {
      return
    }
    const customerId = getCustomerId(supplierRef, inboundTicketData, customers, data)

    const inboundTicketSave = formatData(
      data,
      customerId,
      inboundTicketData?.ticketId,
      inboundTicketData?.paymentId,
      ticketHashRef?.current?.value,
    )

    console.log('=== Final payload being sent to backend ===')
    console.log('Stringified payload:', JSON.stringify(inboundTicketSave, null, 2))

    // Update the total after formatting data (use calculated total including fees/promotions for display)
    handleTotalUpdate(calculateTicketTotal())
    if (customerId === customerDataRef.current.id) {
      const contactHasViolation = await checkDoNotBuyViolation(customerDataRef.current?.contacts)
      if (contactHasViolation) {
        dispatchSnackbar(dispatch, 'error', 'Customer is on the Ohio Do Not Buy list. Transaction prohibited.')
        return
      }
    }
    if (isUpdate) {
      await updateInboundTicket({
        ticketId: inboundTicketId,
        billId: inboundTicketData?.billId,
        inboundTicketSave,
      })
        .unwrap()
        .then(resIBT => {
          handleSuccess({ resIBT, data, customerId, saveAction })
        })
        .catch(error => {
          handleError({ error })
        })
    } else {
      // Create new ticket
      // On new tickets, inboundTicketNotesRef.current is null until the first note is added, so we need to check for that
      const newNotes =
        inboundTicketNotesRef.current
          ?.map(note => ({
            name: note.note,
            internal: note.isInternal,
          }))
          ?.filter(note => note.note !== '') ?? []

      await createInboundTicket({
        inboundTicketSave: {
          ...inboundTicketSave,
          notes: newNotes,
          ...{ idempotencyKey: idempotencyKey?.current },
        },
      })
        .unwrap()
        .then(resIBT => {
          handleSuccess({ resIBT, data, customerId, isNew: true, saveAction })
        })
        .catch(error => {
          handleError({ error, isNew: true })
        })
    }
  }

  useEffect(() => {
    const formattedFreightCost = `${carrierPricePrepend}${setDecimalPrecision(cost || 0, {
      decimalPrecisionOverride: 2,
    })}`
    setValue('footerInformation.freightCost', formattedFreightCost)
    setFreightCostFieldLength(formattedFreightCost.length)
  }, [cost, carrierPricePrepend])

  /**
   * fill in form data from inboundTicketData
   */
  useEffect(() => {
    if (isDirty || hasEditedMaterials || purchaseOrderShouldDirty.current) return
    if (!feesAndPromotionsData) return
    if (inboundTicketData) {
      setValue('customer.name', inboundTicketData?.customer)
      setValue('customer.passengers', inboundTicketData?.numberOfPassengers)
      setValue('customer.carrierNumber', inboundTicketData?.transportationInfo?.carrierNumber)
      setValue('customer.effectiveDate', new Date(inboundTicketData?.effectiveDate))
      setValue('status.value', inboundTicketData?.status?.value)
      setValue('customer.contactId', inboundTicketData?.contactId || '')

      const { formattedScrapVehicles = [], scrapVehiclesAsMaterials = [] } = formatScrapVehicles(
        inboundTicketData.scrapVehicles,
      )

      const formattedMaterials = [...(inboundTicketData?.materials || []), ...scrapVehiclesAsMaterials]
        .sort((a, b) => {
          if (!a.rowIndex) return 1
          if (!b.rowIndex) return -1
          return a.rowIndex - b.rowIndex
        })
        .map((material, index) => {
          const { materialType, orderEntryId } = material
          const orderEntry = purchaseOrderData?.priceSheet?.entriesObj?.find(entry => entry.id === orderEntryId)
          return {
            ...material,
            materialCode: material?.materialCode || materialType?.code,
            materialName: material?.materialName || materialType?.commonName,
            overrideName: orderEntry?.overrideName,
            rowIndex: index + 1,
            finishedGoodTagNumber: material?.finishedGood?.tag,
            finishedGoodItemCount: material?.finishedGood?.itemCount,
          }
        })

      dispatch(setScrapVehicles(formattedScrapVehicles))
      setInboundTicketMaterials(formattedMaterials)
      const grossValues = {
        ...inboundTicketData?.truckWeight?.gross,
        grossImages: inboundTicketData?.status?.grossImages,
      }

      const tareValues = {
        ...inboundTicketData?.truckWeight?.tare,
        tareImages: inboundTicketData?.status?.tareImages,
      }

      setTruckWeight({
        ...inboundTicketData?.truckWeight,
        gross: grossValues,
        tare: tareValues,
      })
      const { purchaseOrderNumber, purchaseOrderId } = inboundTicketData?.transportationInfo || {}
      const formattedPurchaseOrderNumber = purchaseOrderNumber?.toString() || ''

      dispatch(
        setPurchaseOrderInfo({
          purchaseOrderNumber: formattedPurchaseOrderNumber,
          purchaseOrderId: purchaseOrderId || '',
        }),
      )
      setValue('transportationInfo.purchaseOrderNumber', formattedPurchaseOrderNumber)
      setValue('transportationInfo.purchaseOrderId', purchaseOrderId || '')
      setValue('transportationInfo.billOfLading', inboundTicketData.transportationInfo?.billOfLading || '')
      setValue('transportationInfo.carrierName', inboundTicketData.transportationInfo?.carrierName || '')
      setValue('transportationInfo.carrierLookup', inboundTicketData.carrierId === carrierData?.id ? carrierData : null)
      setValue('transportationInfo.containerNumber', inboundTicketData.transportationInfo?.containerNumber || '')
      setValue('transportationInfo.driverName', inboundTicketData.transportationInfo?.driverName || '')
      setValue('transportationInfo.isPickup', inboundTicketData.transportationInfo?.isPickup || 'false')
      setValue('transportationInfo.trailerNumber', inboundTicketData.transportationInfo?.trailerNumber || '')
      setValue('transportationInfo.transportationType', inboundTicketData.transportationInfo?.transportationType || '')
      setValue('freightCost.cost', inboundTicketData.freightCost?.cost || 0)
      setValue('freightCost.chargeBack', inboundTicketData.freightCost?.chargeBack || 0)
      setValue('freightCost.userSet', inboundTicketData.freightCost?.userSet || false)
      setValue('freightCost.userChargebackSet', inboundTicketData.freightCost?.userChargebackSet || false)
      setValue('freightCost.id', inboundTicketData.freightCost?.id || '')
      setValue('notes', inboundTicketData.notes || [])
      setValue('docs', uniqBy(inboundTicketData.docs || [], 'uuid'))
      setValue('uploadcareGroupId', null)
      setValue('truckWeight', {
        ...(inboundTicketData.truckWeight || truckWeight),
        gross: grossValues,
        tare: tareValues,
      })
      setValue('axleWeights', inboundTicketData?.axleWeights || [])
      const truckTotal = getTruckTotal(inboundTicketData)
      const differential = truckTotal - inboundTicketData.netWeight
      setDifferentialAmount(differential)

      if (vehicleData) {
        const formattedVehicleData = formatVehicle(vehicleData)
        setValue('customer.vehicle', formattedVehicleData)
        if (!supplierRef?.current?.id) {
          setCustomerVehicle(formattedVehicleData)
        } else if (!!supplierRef?.current.id && formattedVehicleData.customerId === supplierRef?.current.id) {
          setCustomerVehicle(formattedVehicleData)
        } else if (supplierRef?.current?.id === defaultRetailCustomer?.id) {
          setCustomerVehicle({
            ...formattedVehicleData,
            customerId: defaultRetailCustomer?.id,
          })
        }
      }
      if (inboundTicketData?.feeOrPromotion?.length > 0) {
        // set f&p data to form
        const feesAndPromotionsRows = inboundTicketData.feeOrPromotion.map(item => ({
          id: item.settingId,
          name: item.name,
          type: item.type,
          amountType: item.amountType,
          amount: item.amount,
          eligiblePaymentTypes: item.eligiblePaymentTypes,
        }))
        setValue('feeOrPromotion', keyBy(feesAndPromotionsRows, 'id'))
      }
    } // HANDLE NEW IBT
    else if (!isUpdate) {
      setValue('freightCost.userSet', false)
      setValue('freightCost.userChargebackSet', false)
      setValue('transportationInfo.transportationType', TRANSPORTATION_TYPE_OPTIONS[1].id)

      // set f&p data to form
      const feesAndPromotionsRows = feesAndPromotionsData?.Items?.map(item => ({
        id: item.id,
        name: item.name,
        type: item.type,
        amountType: item.amountType,
        amount: item.amount,
        eligiblePaymentTypes: item.eligiblePaymentTypes,
      }))
      setValue('feeOrPromotion', keyBy(feesAndPromotionsRows, 'id'))

      if (enableAxleWeights) {
        setValue(
          'axleWeights',
          [...Array(defaultNumberOfAxles).keys()].map((_, index) => ({ ...DEFAULT_AXLE_DATA, axleNumber: index + 1 })),
        )
      } else {
        setValue('axleWeights', [])
      }
    }
  }, [inboundTicketData, carrierData, vehicleData, purchaseOrderData, feesAndPromotionsData])

  const { isAutoSaving } = useAutosave(
    {
      saveFunction: methods.handleSubmit(onSubmit),
      trigger:
        isAutosaveEnabled &&
        userIsInactive &&
        materialEntryViewMode !== SCALE_MATERIAL_VIEW_OPTIONS.MATERIAL_ENTRY_NEW &&
        materialEntryViewMode !== SCALE_MATERIAL_VIEW_OPTIONS.MATERIAL_ENTRY_UPDATE &&
        materialEntryViewMode !== SCALE_MATERIAL_VIEW_OPTIONS.TRUCK_ENTRY &&
        (isCustomerInfoDirty || hasEditedMaterials) &&
        Object.keys(errors).length === 0 &&
        !!supplierRef?.current?.id,
    },
    [inboundTicketData],
  )

  useEffect(() => {
    wrappedUpdateTicketActionsAndStatus(onSubmit, hasDataConflict, isAutoSaving)
  }, [
    inboundTicketData,
    billData,
    isDirty,
    hasEditedMaterials,
    errors,
    isUpdate,
    isAutoSaving,
    truckWeight,
    hasDataConflict,
  ])

  // Helper function to calculate ticket total using the same logic as the inbound tickets list
  const calculateTicketTotal = useCallback(() => {
    // Calculate material net values the same way as formatData function
    const materialNetValue = inboundTicketMaterials.reduce((aggregate, material) => {
      const materialValue = material?.weightAndPrice?.netValue ? Dinero(material.weightAndPrice.netValue).toUnit() : 0
      return roundTo(aggregate + materialValue, ROUNDING_PRECISIONS.COST)
    }, 0)

    // Get freight chargeback
    const freightChargeback = getValues('freightCost.chargeBack') || 0

    // Calculate fees and promotions using IBT netCost (same as backend)
    const feeOrPromotionFormState = getValues('feeOrPromotion') || {}
    const feeOrPromotionData = Object.values(feeOrPromotionFormState)

    let totalFeesAndPromotions = 0
    feeOrPromotionData.forEach(item => {
      const calculatedValue = calculateFeePromotionValue(item, materialNetValue)
      totalFeesAndPromotions += calculatedValue
    })

    // Return the final netValue (same calculation as what gets saved and displayed in list)
    return materialNetValue - freightChargeback + totalFeesAndPromotions
  }, [inboundTicketMaterials, getValues])

  // Calculate and update total when materials or fees change
  useEffect(() => {
    const total = calculateTicketTotal()

    if (currentTotal !== total) {
      handleTotalUpdate(total)
    }
  }, [inboundTicketMaterials, feesAndPromotionsFormatted, watch('freightCost.chargeBack'), calculateTicketTotal])

  // Update actions when total changes (for dynamic PAY/INVOICE button)
  useEffect(() => {
    if (onSubmit) {
      wrappedUpdateTicketActionsAndStatus(onSubmit, hasDataConflict, isAutoSaving, currentTotal)
    }
  }, [currentTotal])

  let loadNotes = []
  if (inboundTicketNotes) {
    loadNotes = formatNotes(inboundTicketNotes.map(note => ({ ...note, name: note.note })))
  }

  useEffect(() => {
    setValue('customer.id', `#${customerData?.externalId}` || '---')
    setValue('customer.supplierType', customerData?.supplierType || '---')
    setValue('customer.buysForbidden', customerData?.buysForbidden ? 'No Buy' : undefined)
  }, [customerData])

  useEffect(() => {
    if (supplier?.id === customerData?.id) {
      // Set location options
      const validCustomerLocations = customerData?.locations?.filter(location => locationIsNotEmpty(location)) || []
      setLocationOptions(validCustomerLocations)
      const prioritizedLocationId = isDirty ? purchaseOrderLocationId : inboundTicketData?.locationId
      if (validCustomerLocations.findIndex(location => location.id === prioritizedLocationId) > -1) {
        setValue('customer.locationId', prioritizedLocationId)
      } else {
        const currentlySelectedLocationId = getValues('customer.locationId')
        if (
          !currentlySelectedLocationId ||
          validCustomerLocations.findIndex(location => location.id === currentlySelectedLocationId) === -1
        ) {
          setValue('customer.locationId', validCustomerLocations[0]?.id || '')
        }
      }

      // Set contact options
      if (customerData?.contacts?.length > 0) {
        setContactOptions(
          customerData.contacts.map(contact => ({
            id: contact.id,
            name: `${contact.firstName || ''} ${contact.lastName || ''}`.trim(),
            email: contact.email,
            phone: contact.phone,
          })),
        )
      } else {
        setContactOptions([])
      }
    }
  }, [customerData, inboundTicketData, purchaseOrderLocationId, supplier?.id])

  useEffect(() => {
    if (supplier?.id === customerData?.id) {
      if (customerData?.supplierType && !supplier?.supplierType) {
        dispatch(setCustomer({ ...supplier, supplierType: customerData?.supplierType }))
      }
    }
  }, [customerData])

  const onClickMaterialRow = id => {
    if (statusIsPaidOrVoided() || hasDataConflict || isInLockedAccountingPeriodV1) return
    const feeOrPromotionFormVal = getValues(`feeOrPromotion.${id}`)
    if (id === 'differential') {
      setOpenSplitWeightsBlade(true)
    } else if (id === 'truck') {
      setMaterialEntryViewMode(SCALE_MATERIAL_VIEW_OPTIONS.TRUCK_ENTRY)
    } else if (feeOrPromotionFormVal) {
      setSelectedFeeOrPromotionId(id)
    } else {
      const material = inboundTicketMaterials.find(mat => mat.id === id)
      setSelectedMaterial(material)
      setMaterialEntryViewMode(SCALE_MATERIAL_VIEW_OPTIONS.MATERIAL_ENTRY_UPDATE)
    }
  }

  const onDeleteTableRow = id => {
    if (statusIsPaidOrVoided() || hasDataConflict || isInLockedAccountingPeriodV1) return
    const feeOrPromotionFormVal = getValues(`feeOrPromotion.${id}`)
    if (feeOrPromotionFormVal) {
      const current = getValues('feeOrPromotion')
      const updated = { ...current }
      delete updated[id]
      setValue('feeOrPromotion', updated, {
        shouldDirty: true,
      })
    }
    // todo implement inline row icon based delete for other things that go into the table
  }

  const validateSupplier = () => {
    if (supplier.customerCommonIdentifierString) return true
    if (selectedCustomer) {
      const { id, customerCommonIdentifierString, supplierType } = selectedCustomer
      dispatch(setCustomer({ id, customerCommonIdentifierString, supplierType }))
    }
    return false
  }

  useEffect(() => {
    setHasSupplier(validateSupplier())
  }, [supplier, selectedCustomer])

  const updateMaterialEntryViewMode = mode => setMaterialEntryViewMode(mode)

  const updateTruckWeight = ({ axleWeights, ...truck }) => {
    if (truck) setTruckWeight(truck)
    setValue('truckWeight', truck)
    setValue('axleWeights', axleWeights)
    setHasEditedMaterials(true)
  }

  const updateMaterialRow = useCallback(
    material => {
      if (inboundTicketMaterials.findIndex(m => m.id === material.id) >= 0) {
        setInboundTicketMaterials(inboundTicketMaterials.map(m => (m.id === material.id ? material : m)))
      } else {
        setInboundTicketMaterials(prevIBTMaterials => [
          ...prevIBTMaterials,
          { ...material, rowIndex: prevIBTMaterials.length + 1 },
        ])
      }
      setHasEditedMaterials(true)
    },
    [inboundTicketMaterials],
  )

  const deleteMaterialRow = useCallback(
    materialId => {
      setInboundTicketMaterials(prevIBTMaterials =>
        prevIBTMaterials
          .filter(m => m.id !== materialId)
          .map((material, index) => ({ ...material, rowIndex: index + 1 })),
      )
      setHasEditedMaterials(true)
    },
    [inboundTicketMaterials],
  )

  const onUploadFiles = useCallback(uploadUUID => {
    setValue('uploadcareGroupId', uploadUUID)
    onSubmit(getValues())
  }, [])

  const isUploadFilesDisabled = () => {
    if (isUpdate) return !isValid
    return true
  }

  const onRemoveFiles = files => {
    setValue('docs', files)
    onSubmit({ ...getValues(), docs: files })
  }

  useEffect(() => {
    if (initialRender.current) return

    const { rowId, vin, year, color, make, model } = newScrapVehicle
    if (vin) {
      const vehicle = {
        id: rowId,
        vin,
        rowIndex: inboundTicketMaterials.length + 1,
        rowType: IBT_ROW_TYPES.SCRAP_VEHICLE,
        materialCode: 'scrap-vehicle',
        materialName: `${year} ${color} ${make} ${model}`.replace(/\s\s+/g, ' ').trim(),
      }
      if (inboundTicketMaterials.findIndex(m => m.id === vehicle.id) >= 0) {
        setInboundTicketMaterials(
          inboundTicketMaterials.map(m => (m.id === vehicle.id ? { ...vehicle, rowIndex: m.rowIndex } : m)),
        )
      } else {
        setInboundTicketMaterials([...inboundTicketMaterials, vehicle])
      }
      setHasEditedMaterials(true)
    }
  }, [newScrapVehicle])

  const formPurchaseOrderId = watch('transportationInfo.purchaseOrderId')

  useEffect(() => {
    if (!isDirty) return // skip on initial page load
    if (formPurchaseOrderId && formPurchaseOrderId !== purchaseOrderData?.id) return // skip if still fetching new purchaseOrderData
    setInboundTicketMaterials(prevIBTMaterials =>
      prevIBTMaterials.map(row => {
        // use the first entry from the new purchase order that matches the material's materialTypeId
        const purchaseOrderEntry = formPurchaseOrderId
          ? purchaseOrderData?.priceSheet?.entriesObj?.find(entry => entry.materialTypeId === row.materialTypeId)
          : null
        return {
          ...row,
          orderEntryId: purchaseOrderEntry?.id || null,
          overrideName: purchaseOrderEntry?.overrideName || '',
        }
      }),
    )
  }, [formPurchaseOrderId, purchaseOrderData])

  const [anchorEl, setAnchorEl] = useState(null)
  const handlePopoverOpen = event => {
    setAnchorEl(event.currentTarget)
  }

  const handlePopoverClose = () => {
    setAnchorEl(null)
  }

  const renderSupplierName = () => {
    if (hasSupplier) {
      return (
        <Grid container spacing={1}>
          <Grid item xs={10}>
            <Stack
              direction="column"
              alignItems="start"
              spacing={1}
              border="1px solid"
              borderColor="grey.400"
              borderRadius={1}
              p={1}
              onClick={handlePopoverOpen}
              sx={{
                cursor: 'pointer',
                '&:hover': {
                  borderColor: 'text.primary',
                },
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <LicenseAvatar selectedCustomer={selectedCustomer || null} customerData={customerData} />
                <Typography variant="body1" color="textPrimary">
                  {supplier?.customerCommonIdentifierString}
                </Typography>
                {customerData?.notes && (
                  <Tooltip title={<div style={{ whiteSpace: 'pre-line' }}>{customerData?.notes}</div>}>
                    <NoteIcon color="info" fontSize="small" />
                  </Tooltip>
                )}
              </Stack>
              {isDateInPast(customerData?.contacts?.[0]?.licenseExpiration) && (
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Tooltip title="License Expired">
                    <WarningIcon color="error" fontSize="small" />
                  </Tooltip>
                  <Typography variant="body2" color="text.secondary">
                    License Expired
                  </Typography>
                </Stack>
              )}
              {!purchaseOrderNumber &&
                customerData?.id &&
                (isFetchingPOs ? (
                  <CircularProgress size={20} />
                ) : (
                  <Typography variant="caption" color={hasPurchaseOrders ? 'success.main' : 'text.secondary'}>
                    {hasPurchaseOrders
                      ? t('purchase_orders_available_count', { count: purchaseOrdersData?.totalResultsCount })
                      : t('no_purchase_orders')}
                  </Typography>
                ))}
            </Stack>
          </Grid>
          <Grid item xs={2} alignSelf="center" textAlign="center">
            <Box>
              {!isTransfer && !statusIsPaidOrVoided() && !hasDataConflict && !isInLockedAccountingPeriodV1 && (
                <IconButton color="error" onClick={handleRemoveCustomer}>
                  <DeleteIcon />
                </IconButton>
              )}
            </Box>
          </Grid>
        </Grid>
      )
    }

    return (
      <Grid container>
        <Grid item xs={canAddCustomer ? 10 : 12} md={canAddCustomer ? 10 : 12}>
          <CustomerAutocomplete
            name="customer.name"
            label={t('supplier_name')}
            data-test="customer-name"
            variant="outlined"
            control={control}
            freeSolo={false}
            error={!!errors.customer?.name}
            helperText={errors.customer?.name?.message}
            baseFilter={CUSTOMERS_FILTERS.SUPPLIER}
            autocompleteProps={{
              renderOption: (props, option) => (
                <CustomerLicenseListItem props={props} option={option} key={option.id} />
              ),
              filterOptions: options => options,
            }}
          />
        </Grid>
        {canAddCustomer && (
          <Grid item xs={2} md={2} alignSelf="center" textAlign="center">
            <Button
              variant="text"
              startIcon={<AddCircleIcon />}
              onClick={() =>
                dispatch(
                  showFormModal({
                    formModalName: FORM_MODALS.NEW_CUSTOMER,
                    formModalProps: { type: 'supplier' },
                  }),
                )
              }
            />
          </Grid>
        )}
      </Grid>
    )
  }

  useEffect(() => {
    initialRender.current = false
  }, [])

  const onSaveDifferential = materials => {
    setInboundTicketMaterials(materials)
    setHasEditedMaterials(true)
  }

  const preparedPropIbtData = useMemo(() => {
    if (inboundTicketData && ticketHashRef?.current?.value) {
      const customerId = getCustomerId(supplierRef, inboundTicketData, customers)

      return formatData(
        inboundTicketData,
        customerId,
        inboundTicketData?.ticketId,
        inboundTicketData?.paymentId,
        ticketHashRef?.current?.value,
      )
    }
  }, [inboundTicketData, ticketHashRef?.current?.value])

  const { priorityFreightCostInfo, isLoading: isLoadingFreightLanesData } = useCalculateFreightCost({
    location: customerDataRef?.current?.locations?.find(location => location.id === customerLocationId),
    transportationType,
    carrierId: selectedCarrier?.id || '',
    effectiveDate: getValues('customer.effectiveDate')?.toISOString(),
    netWeight: netWeight || 0,
    ticketType: 'IBT',
    contract: null, // Purchase orders don't have freight costs yet
  })

  const disableFreightCost = freightBillIsPaid || statusIsVoided() || isInLockedAccountingPeriodV1
  const disableChargeback =
    statusIsPaidOrVoided() || isInLockedAccountingPeriodV1 || billData?.status === BILL_STATUSES.INVOICED.id
  /**
   * Apply freight cost logic depending on automatic chargeback and user set values
   */
  useEffect(() => {
    if (!freightBillIsPaid && isDirty) {
      if (userSet === false && !disableFreightCost) {
        setValue('freightCost.cost', priorityFreightCostInfo?.freightCost, { shouldDirty: true })
      }
      if (!statusIsPaidOrVoided() && userChargebackSet === false && !disableChargeback) {
        const chargeBackValue = priorityFreightCostInfo?.freightChargeback ?? 0
        setValue('freightCost.chargeBack', chargeBackValue, { shouldDirty: true })
      }
    }
  }, [isDirty, freightBillIsPaid, priorityFreightCostInfo, disableFreightCost, disableChargeback])

  // Add query to check for available POs
  const { data: purchaseOrdersData, isFetching: isFetchingPOs } = useGetPurchaseOrdersV2Query(
    {
      page: 1,
      pageSize: 1,
      filter: JSON.stringify({
        supplierId: customerData?.id,
        statuses: ['Open'],
      }),
    },
    {
      skip: !customerData?.id,
    },
  )

  const hasPurchaseOrders = purchaseOrdersData?.totalResultsCount > 0 && customerData?.id

  // Update contact options when customer data changes
  useEffect(() => {
    if (customerData?.contacts?.length > 0) {
      setContactOptions(
        customerData.contacts.map(contact => ({
          id: contact.id,
          name: `${contact.firstName || ''} ${contact.lastName || ''}`.trim(),
          email: contact.email,
          phone: contact.phone,
        })),
      )
    } else {
      setContactOptions([])
    }
  }, [customerData])

  return (
    <>
      <AlertModal
        open={showRemoveCustomerModal}
        modalType="remove"
        title="Remove Customer"
        question="Are you sure you want to remove this customer?"
        description="Removing this customer will also remove the purchase order from this ticket."
        confirmLabel="YES, REMOVE THIS CUSTOMER"
        cancelLabel="GO BACK"
        onClose={() => {
          setShowRemoveCustomerModal(false)
        }}
        onSubmit={() => {
          removeCustomer()
          removePurchaseOrder()
          setShowRemoveCustomerModal(false)
        }}
      />
      <SplitTruckAndMaterialDifferentialBlade
        differentialAmount={differentialAmount}
        openBlade={openSplitWeightsBlade}
        setOpenBlade={setOpenSplitWeightsBlade}
        materials={inboundTicketMaterialsRef.current}
        onSave={onSaveDifferential}
        viewMode={IBT_VIEW_OPTIONS.SCALE}
        purchaseOrderData={purchaseOrderData}
      />
      <FreightCostBlade
        open={openFreightCostBlade}
        setOpen={setOpenFreightCostBlade}
        getValues={getValues}
        setValue={setValue}
        ticketStatus={inboundTicketData?.status?.value}
        netWeight={netWeight}
        location={locationOptions?.find(location => location.id === customerLocationId)}
        type="IBT"
        pricePrepend={pricePrepend}
        freightBillData={inboundTicketData?.freightCost?.freightBill || null}
        isInLockedAccountingPeriodV1={isInLockedAccountingPeriodV1}
        disableChargeback={disableChargeback}
      />
      <Stack direction={{ xs: 'column', md: 'row' }} p={3} spacing={3}>
        <Box
          width={{ xs: '100%', md: '25%' }}
          sx={{
            border: 1,
            borderColor: 'grey.400',
            borderRadius: 2,
            backgroundColor: 'background.section',
          }}
          p={2}
          data-testid="customer-info"
        >
          <Typography variant="h6" gutterBottom color="textSecondary">
            {t('customer_information')}
          </Typography>
          <Stack paddingTop={1} spacing={2}>
            {renderSupplierName()}
            <Button
              data-testid="customer-purchaseOrder-button"
              disabled={statusIsPaidOrVoided() || hasDataConflict || isInLockedAccountingPeriodV1}
              variant="outlined"
              sx={{
                border: '1px solid #009F4D80',
                fontWeight: 'bold',
              }}
              startIcon={purchaseOrderNumber ? <EditIcon /> : <LinkIcon />}
              onClick={() => {
                purchaseOrderShouldDirty.current = true
                dispatch(
                  showFormModal({
                    formModalName: FORM_MODALS.LINK_PURCHASE_ORDER,
                  }),
                )
              }}
            >
              {purchaseOrderNumber ? `${t('po_number')} ${purchaseOrderNumber}` : t('link_purchase_order')}
            </Button>
            {hasSupplier && (
              <Stack direction="row" onClick={handlePopoverOpen}>
                <Input
                  name="customer.id"
                  label={t('customer_id')}
                  data-test="customer-id"
                  variant="standard"
                  size="small"
                  defaultValue=""
                  control={control}
                  InputProps={{
                    readOnly: true,
                    disableUnderline: true,
                  }}
                />
                <Input
                  name="customer.supplierType"
                  label={t('type')}
                  data-test="customer-type"
                  variant="standard"
                  size="small"
                  defaultValue=""
                  control={control}
                  InputProps={{
                    readOnly: true,
                    disableUnderline: true,
                    inputProps: {
                      style: { textTransform: 'capitalize' },
                      disabled: statusIsPaidOrVoided() || hasDataConflict || isInLockedAccountingPeriodV1,
                    },
                  }}
                />
                {customerData?.buysForbidden && (
                  <Input
                    name="customer.buysForbidden"
                    label={t('buy')}
                    data-test="customer-buysForbidden"
                    variant="standard"
                    size="small"
                    defaultValue=""
                    control={control}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <WarningIcon color="warning" fontSize="small" />
                        </InputAdornment>
                      ),
                      readOnly: true,
                      disableUnderline: true,
                    }}
                  />
                )}
              </Stack>
            )}

            <Controller
              name="customer.contactId"
              control={control}
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <Autocomplete
                  options={contactOptions}
                  getOptionLabel={option => option.name || ''}
                  value={contactOptions.find(opt => opt.id === value) || null}
                  onChange={(_, newValue) => onChange(newValue?.id || '')}
                  disabled={
                    statusIsPaidOrVoided() ||
                    !hasSupplier ||
                    contactOptions.length === 0 ||
                    hasDataConflict ||
                    isInLockedAccountingPeriodV1
                  }
                  renderInput={params => (
                    <TextField
                      {...params}
                      label={t('contact')}
                      variant="outlined"
                      size="small"
                      error={!!errors.customer?.contactId}
                      helperText={errors.customer?.contactId?.message}
                      data-test="customer-contact-id"
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Stack direction="column" spacing={0.5}>
                        <Typography variant="body2">{option.name}</Typography>
                        {option.email && (
                          <Typography variant="caption" color="text.secondary">
                            {option.email}
                          </Typography>
                        )}
                        {option.phone && (
                          <Typography variant="caption" color="text.secondary">
                            {option.phone}
                          </Typography>
                        )}
                      </Stack>
                    </li>
                  )}
                />
              )}
            />

            <Controller
              name="customer.locationId"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Autocomplete
                  options={locationOptions}
                  getOptionLabel={option => option.name || ''}
                  value={locationOptions.find(opt => opt.id === value) || null}
                  onChange={(_, newValue) => onChange(newValue?.id || '')}
                  disabled={
                    statusIsPaidOrVoided() ||
                    !hasSupplier ||
                    hasDataConflict ||
                    isInLockedAccountingPeriodV1 ||
                    locationOptions.length === 0
                  }
                  renderInput={params => (
                    <TextField
                      {...params}
                      label={t('location')}
                      variant="outlined"
                      size="small"
                      error={!!errors.customer?.locationId}
                      helperText={errors.customer?.locationId?.message}
                      data-test="customer-location-id"
                    />
                  )}
                  disableClearable
                />
              )}
            />
            <CustomerInfoPopover selectedCustomer={customerData} anchorEl={anchorEl} onClose={handlePopoverClose} />
            <DatePickerV2
              name="customer.effectiveDate"
              label={t('effective_date')}
              data-test="customer-effectivedate"
              variant="outlined"
              size="small"
              control={control}
              defaultDate={new Date()}
              datePickerProps={{
                disabled: statusIsPaidOrVoided() || hasDataConflict || isInLockedAccountingPeriodV1,
                minDate: lockAccountingPeriod.isLockedForAccounting
                  ? addToDate(new Date(lockAccountingPeriod.lockAccountingDate))
                  : undefined,
              }}
              InputLabelProps={{
                shrink: true,
              }}
              error={!!errors.customer?.effectiveDate}
              helperText={errors.customer?.effectiveDate?.message}
            />
            <Input
              name="customer.passengers"
              label={t('number_of_passengers')}
              data-test="customer-passengers"
              type="number"
              pattern="[0-9]*"
              variant="outlined"
              size="small"
              defaultValue=""
              fullWidth
              control={control}
              disabled={statusIsPaidOrVoided() || hasDataConflict || isInLockedAccountingPeriodV1}
            />
            <Grid container direction="row" flexGrow={1} alignItems="center" justifyContent="space-between">
              <Grid item md={10} xs={11} pr={1}>
                <VehicleAutocomplete
                  name="customer.vehicle"
                  hasSupplier={hasSupplier}
                  control={control}
                  size="small"
                  data-test="customer-vehicle"
                  customerVehicle={customerVehicle}
                  setCustomerVehicle={setCustomerVehicle}
                  setValue={setValue}
                  disabled={statusIsPaidOrVoided() || hasDataConflict || isInLockedAccountingPeriodV1}
                  customerVehicleOptions={
                    selectedCustomer?.id &&
                    customerData?.id === selectedCustomer?.id &&
                    customerData?.vehicles?.length > 0
                      ? customerData?.vehicles
                      : []
                  }
                  customerId={selectedCustomer?.id}
                />
              </Grid>
              <Grid item md={2} xs={1}>
                <IconButton
                  id="add-customer-vehicle-button"
                  onClick={() => onAddVehicle('customer-vehicle')}
                  disabled={statusIsPaidOrVoided() || hasDataConflict || isInLockedAccountingPeriodV1}
                >
                  <AddCircleIcon />
                </IconButton>
              </Grid>
            </Grid>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1.5}>
              <NotesInput
                onClickNotes={() => setOpenNotes(true)}
                notes={loadNotes}
                sx={{ width: '100%' }}
                size={'small'}
              />
              {canUploadFilesIBT && (
                <FileUploader
                  onUploadFiles={onUploadFiles}
                  onRemoveFiles={onRemoveFiles}
                  filesData={uniqBy(inboundTicketData?.docs || [], 'uuid')}
                  disabled={isUploadFilesDisabled() || hasDataConflict || isInLockedAccountingPeriodV1}
                  tooltipMsg={t('attach_files_tooltip')}
                />
              )}
            </Stack>
            <NotesModal
              notes={loadNotes}
              open={openNotes}
              onSave={onSaveNotesLocal}
              onClose={() => setOpenNotes(false)}
              disabled={hasDataConflict}
            />
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
              <Typography variant="h6" gutterBottom color="textSecondary">
                {t('transportation_info')}
              </Typography>
            </Stack>
            {appRouteLoadBoard && (
              <DispatchTicketFormArea
                inboundTicketData={inboundTicketData}
                preparedPropIbtData={preparedPropIbtData}
                dispatch={dispatch}
                statusIsPaidOrVoided={statusIsPaidOrVoided}
                setShowUnlinkModal={setShowUnlinkModal}
                materialTypesData={materialTypesData}
                disabled={statusIsVoided() || hasDataConflict || isInLockedAccountingPeriodV1}
                formData={formatData(getValues())}
              />
            )}
            <Grid container direction="row" flexGrow={1} alignItems="center" justifyContent="space-between">
              <Grid item xs={8} pr={2}>
                <CustomerAutocomplete
                  name="transportationInfo.carrierLookup"
                  label={t('carrier')}
                  data-test="transportation-info-carrier-name"
                  variant="outlined"
                  size="small"
                  disabled={statusIsVoided() || freightBillIsPaid || hasDataConflict || isInLockedAccountingPeriodV1}
                  control={control}
                  error={!!errors.transportationInfo?.carrierLookup}
                  helperText={errors.transportationInfo?.carrierLookup?.message}
                  fullWidth
                  freeSolo={false}
                  baseFilter={CUSTOMERS_FILTERS.CARRIER}
                  autocompleteProps={{
                    renderOption: (props, option) => (
                      <li {...props} key={option.id}>
                        {option.displayName}
                      </li>
                    ),
                    isOptionEqualToValue: (option, value) => option?.id === value?.id,
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                {isLoadingFreightLanesData ? (
                  <>
                    <InputLabel shrink>Freight</InputLabel>
                    <Skeleton animation="wave" sx={{ width: `${freightCostFieldLength || 0}ch` }} />
                  </>
                ) : (
                  <Input
                    disabled={hasDataConflict}
                    name="footerInformation.freightCost"
                    label={`${t('freight')}:`}
                    control={control}
                    variant="standard"
                    size="small"
                    defaultValue="$0.00"
                    sx={{
                      '& .MuiInput-root': {
                        cursor: 'unset',
                      },
                      input: {
                        WebkitTextFillColor: theme.palette.primary.main,
                        fontWeight: 500,
                        width: `${(freightCostFieldLength || 0) + 1}ch`,
                        textDecoration: 'underline',
                        cursor: 'pointer',
                      },
                      label: {
                        WebkitTextFillColor: theme.palette.text.secondary,
                      },
                    }}
                    InputProps={{
                      readOnly: true,
                      disableUnderline: true,
                      inputProps: {
                        onClick: () => setOpenFreightCostBlade(true),
                      },
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                )}
              </Grid>
            </Grid>
            <Input
              name="customer.carrierNumber"
              label={t('truck_number')}
              data-test="transportation-info-truck-number"
              variant="outlined"
              size="small"
              defaultValue=""
              fullWidth
              control={control}
              disabled={statusIsVoided() || hasDataConflict || isInLockedAccountingPeriodV1}
            />
            <Input
              name="transportationInfo.driverName"
              label={t('driver_name')}
              data-test="transportation-info-driver-name"
              variant="outlined"
              size="small"
              defaultValue=""
              disabled={statusIsVoided() || hasDataConflict || isInLockedAccountingPeriodV1}
              control={control}
              error={!!errors.transportationInfo?.driverName}
              helperText={errors.transportationInfo?.driverName?.message}
            />
            <Input
              name="transportationInfo.trailerNumber"
              label={t('trailer_number')}
              data-test="transportation-info-trailer-number"
              variant="outlined"
              size="small"
              defaultValue=""
              disabled={statusIsVoided() || hasDataConflict || isInLockedAccountingPeriodV1}
              control={control}
              error={!!errors.transportationInfo?.trailerNumber}
              helperText={errors.transportationInfo?.trailerNumber?.message}
            />
            <Input
              name="transportationInfo.containerNumber"
              label={t('container_number')}
              data-test="transportation-info-container-number"
              variant="outlined"
              size="small"
              defaultValue=""
              disabled={statusIsVoided() || hasDataConflict || isInLockedAccountingPeriodV1}
              control={control}
              error={!!errors.transportationInfo?.containerNumber}
              helperText={errors.transportationInfo?.containerNumber?.message}
            />
            <Input
              name="transportationInfo.billOfLading"
              label={t('bill_of_lading')}
              data-test="transportation-info-bof"
              variant="outlined"
              size="small"
              defaultValue=""
              disabled={statusIsVoided() || hasDataConflict || isInLockedAccountingPeriodV1}
              control={control}
              error={!!errors.transportationInfo?.billOfLading}
              helperText={errors.transportationInfo?.billOfLading?.message}
            />
          </Stack>
        </Box>
        <Box width={{ xs: '100%', md: '75%' }}>
          {showUnlinkModal && (
            <AlertModal
              open={showUnlinkModal}
              modalType="payment"
              title="Unlink Dispatch Ticket"
              question={`Are you sure you want to unlink Dispatch Ticket #${inboundTicketData?.linkedDispatchTicket?.externalId}?`}
              confirmLabel="UNLINK DISPATCH TICKET"
              cancelLabel="CANCEL"
              description=""
              onClose={() => {
                setShowUnlinkModal(false)
              }}
              onSubmit={handleUnlinkDispatchTicket}
              showIsLoading={isUnlinking}
            />
          )}
          <SimpleMaterialsTable
            materials={inboundTicketMaterials}
            truckWeight={truckWeight}
            type="SCALE_VIEW_TICKET"
            onClickRow={onClickMaterialRow}
            setDifferential={setDifferentialAmount}
            canShowDifferentialRow
            chargeback={chargeback}
            pricePrepend={pricePrepend}
            effectiveDateIBT={effectiveDateInput || getValues('customer.effectiveDate') || null}
            errors={errors}
            isTransfer={isTransfer}
            feesAndPromotions={feesAndPromotionsFormatted}
            onRowDeleteClick={onDeleteTableRow}
          />
          <MaterialEntry
            selectedMaterial={selectedMaterial}
            materials={inboundTicketMaterials}
            setSelectedMaterial={setSelectedMaterial}
            truckWeight={truckWeight}
            materialEntryViewMode={materialEntryViewMode}
            updateMaterialEntryViewMode={updateMaterialEntryViewMode}
            updateMaterialRow={updateMaterialRow}
            deleteMaterialRow={deleteMaterialRow}
            updateTruckWeight={updateTruckWeight}
            onAddVehicle={onAddVehicle}
            isDisabled={statusIsPaidOrVoided() || hasDataConflict || isInLockedAccountingPeriodV1}
            pricePrepend={pricePrepend}
            purchaseOrderData={purchaseOrderData}
            selectedCustomer={selectedCustomer}
            onEditScrapVehicle={onEditScrapVehicle}
            updateInboundTicket={updateInboundTicket}
            inboundTicketData={inboundTicketData}
            clearErrors={clearErrors}
          />
          <FeeOrPromotionModal
            open={!!selectedFeeOrPromotionId}
            onClose={() => setSelectedFeeOrPromotionId(null)}
            selectedFeeOrPromotionId={selectedFeeOrPromotionId}
          />
        </Box>
      </Stack>
    </>
  )
}

InboundTicketScaleContainer.propTypes = {
  isUpdate: PropTypes.bool,
  inboundTicketId: PropTypes.string,
  inboundTicketData: PropTypes.object,
  billData: PropTypes.object,
  customerData: PropTypes.object,
  carrierData: PropTypes.object,
  supplierRef: PropTypes.object,
  customerDataRef: PropTypes.object,
  createInboundTicket: PropTypes.func.isRequired,
  updateInboundTicket: PropTypes.func.isRequired,
  setErrorMessage: PropTypes.func,
  statusIsPaidOrVoided: PropTypes.func,
  freightBillIsPaid: PropTypes.bool,
  isDirty: PropTypes.bool,
  isCustomerInfoDirty: PropTypes.bool,
  hasEditedMaterials: PropTypes.bool,
  setHasEditedMaterials: PropTypes.func,
  wrappedUpdateTicketActionsAndStatus: PropTypes.func,
  isAutosaveEnabled: PropTypes.bool,
  onAddVehicle: PropTypes.func.isRequired,
  vehicleData: PropTypes.object,
  updateVehicleWithCustomerAndIBT: PropTypes.func.isRequired,
  userIsInactive: PropTypes.bool,
  isTransfer: PropTypes.bool,
  pricePrepend: PropTypes.string,
  carrierPricePrepend: PropTypes.string,
  defaultRetailCustomer: PropTypes.object,
  purchaseOrderData: PropTypes.object,
  onEditScrapVehicle: PropTypes.func,
  errorsTareRequired: PropTypes.func,
  setIsFetchingMaterialPrices: PropTypes.func,
  enableFePdfs: PropTypes.bool,
  handlePrint: PropTypes.func,
  purchaseOrderLocationId: PropTypes.string,
  feesAndPromotionsData: PropTypes.object,
  checkDoNotBuyViolation: PropTypes.func,
  onTotalUpdate: PropTypes.func,
}

InboundTicketScaleContainer.defaultProps = {
  isUpdate: false,
  inboundTicketId: null,
  inboundTicketData: null,
  billData: {},
  customerData: null,
  carrierData: null,
  supplierRef: null,
  customerDataRef: null,
  setErrorMessage: () => {},
  statusIsPaidOrVoided: () => {},
  freightBillIsPaid: false,
  isDirty: false,
  isCustomerInfoDirty: false,
  hasEditedMaterials: false,
  setHasEditedMaterials: () => {},
  wrappedUpdateTicketActionsAndStatus: () => {},
  isAutosaveEnabled: false,
  vehicleData: null,
  userIsInactive: false,
  isTransfer: false,
  pricePrepend: '$',
  carrierPricePrepend: '$',
  defaultRetailCustomer: null,
  purchaseOrderData: null,
  onEditScrapVehicle: () => {},
  errorsTareRequired: () => {},
  setIsFetchingMaterialPrices: () => {},
  enableFePdfs: false,
  handlePrint: () => {},
  purchaseOrderLocationId: null,
  feesAndPromotionsData: null,
  checkDoNotBuyViolation: () => {},
  onTotalUpdate: () => {},
}

export default InboundTicketScaleContainer
